import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

// Request deduplication cache
const pendingRequests = new Map();

// Data cache with timestamps
const dataCache = new Map();

// Expose cache for clearing (used by useCacheClear composable)
if (typeof window !== "undefined") {
  window.__VUEX_PRODUCTS_CACHE__ = dataCache;
}

const state = {
  // Products
  products: [],
  loadingProducts: false,

  // Services
  services: [],
  loadingServices: false,

  // Pagination
  productsPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },
  servicesPagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  productsFilters: {
    query: "",
    sort: "name",
  },
  servicesFilters: {
    query: "",
    sort: "name",
  },

  // Error handling
  error: null,
};

const getters = {
  // Products
  products: (state) => state.products,
  loadingProducts: (state) => state.loadingProducts,
  productsPagination: (state) => state.productsPagination,
  productsFilters: (state) => state.productsFilters,

  // Services
  services: (state) => state.services,
  loadingServices: (state) => state.loadingServices,
  servicesPagination: (state) => state.servicesPagination,
  servicesFilters: (state) => state.servicesFilters,

  // Errors
  error: (state) => state.error,
};

const mutations = {
  // Products
  SET_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_LOADING_PRODUCTS(state, loading) {
    state.loadingProducts = loading;
  },
  SET_PRODUCTS_PAGINATION(state, pagination) {
    state.productsPagination = { ...state.productsPagination, ...pagination };
  },
  SET_PRODUCTS_FILTERS(state, filters) {
    state.productsFilters = { ...state.productsFilters, ...filters };
  },

  // Services
  SET_SERVICES(state, services) {
    state.services = services;
  },
  SET_LOADING_SERVICES(state, loading) {
    state.loadingServices = loading;
  },
  SET_SERVICES_PAGINATION(state, pagination) {
    state.servicesPagination = { ...state.servicesPagination, ...pagination };
  },
  SET_SERVICES_FILTERS(state, filters) {
    state.servicesFilters = { ...state.servicesFilters, ...filters };
  },

  // Error handling
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== PRODUCTS ====================

  async fetchProducts(
    { commit, state },
    { storeId, filters = {}, page, perPage, forceRefresh = false } = {}
  ) {
    const pageNum = page || state.productsPagination.page;
    const perPageNum = perPage || state.productsPagination.per_page;
    const cacheKey = `products-${storeId}-${JSON.stringify(
      filters
    )}-${pageNum}-${perPageNum}`;

    // Check cache first (unless forceRefresh) - BEFORE setting loading state
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.products.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached products (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_PRODUCTS", cached.data.items || []);
        commit("SET_PRODUCTS_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating products request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_PRODUCTS", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const params = {
          page: pageNum,
          per_page: perPageNum,
          ...state.productsFilters,
          ...filters,
        };

        const response = await http.get(`/v2/store/product/store/${storeId}`, {
          params,
        });

        const data = response.data || {};

        // Cache the result
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        commit("SET_PRODUCTS", data.items || []);
        commit("SET_PRODUCTS_PAGINATION", {
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          pages: data.pages,
        });

        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message || "Failed to load products";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_PRODUCTS", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // ==================== SERVICES ====================

  async fetchServices(
    { commit, state },
    { storeId, filters = {}, page, perPage, forceRefresh = false } = {}
  ) {
    const pageNum = page || state.servicesPagination.page;
    const perPageNum = perPage || state.servicesPagination.per_page;
    const cacheKey = `services-${storeId}-${JSON.stringify(
      filters
    )}-${pageNum}-${perPageNum}`;

    // Check cache first (unless forceRefresh) - BEFORE setting loading state
    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.services.staleTime;

      if (age < maxAge) {
        console.log(
          `✅ Using cached services (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_SERVICES", cached.data.items || []);
        commit("SET_SERVICES_PAGINATION", {
          page: cached.data.page,
          per_page: cached.data.per_page,
          total: cached.data.total,
          pages: cached.data.pages,
        });
        return cached.data;
      } else {
        console.log(
          `♻️ Cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating services request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_SERVICES", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const params = {
          page: pageNum,
          per_page: perPageNum,
          ...state.servicesFilters,
          ...filters,
        };

        const response = await http.get(`/v2/store/service/store/${storeId}`, {
          params,
        });

        const data = response.data || {};

        // Cache the result
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        commit("SET_SERVICES", data.items || []);
        commit("SET_SERVICES_PAGINATION", {
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          pages: data.pages,
        });

        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message || "Failed to load services";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_SERVICES", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Update filters
  updateProductsFilters({ commit }, filters) {
    commit("SET_PRODUCTS_FILTERS", filters);
  },
  updateServicesFilters({ commit }, filters) {
    commit("SET_SERVICES_FILTERS", filters);
  },

  // Clear cache
  clearProductsCache() {
    dataCache.clear();
    pendingRequests.clear();
    console.log("🗑️ Products cache cleared");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
